import { Banknote, ShieldCheck, BarChart3, BookOpen, ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import MmfYieldSparkline from '@/components/MmfYieldSparkline';

/**
 * MoneyMarketFundOverviewCard — P158·9 (Feb 2026).
 *
 * Renders at the very top of the Insights tab for any ticker matching
 * the strict US MMF shape (3 letters + XX, e.g. SPAXX / FGXXX / VMFXX
 * / SWVXX). Backend stamps `is_money_market_fund` in the profile
 * overlay + admin/mmf-seed back-fill so the flag is available on
 * both fresh and existing cache docs.
 *
 * Purpose: explain that this ticker is NOT a stock or ETF — it is a
 * Money Market Fund that targets a constant $1.00 NAV, generates
 * income by holding short-dated Treasury/repo/government paper, and
 * is used as a broker cash-sweep vehicle. Deliberately does NOT
 * promise the $1.00 NAV — SEC rule 2a-7 permits temporary breaks.
 *
 * Companion:  Triangle tab is hidden for MMFs, the DDM Valuation
 * Matrix is replaced with an N/A note, and the SEO stock page
 * carries the same explainer for search users landing on FGXXX etc.
 */
export default function MoneyMarketFundOverviewCard({ stock }) {
  if (!stock?.is_money_market_fund) return null;
  const family = stock?.mmf_family || null;
  // P158·11 (Feb 2026) — yfinance-sourced yield + AUM (populated by
  // the MMF seed endpoint, gated ONLY to MMF tickers). Silent when
  // fields are absent so unrefreshed seed docs still render cleanly.
  const yield7 = typeof stock?.mmf_7day_yield === 'number' ? stock.mmf_7day_yield : null;
  const aumRaw = typeof stock?.mmf_aum === 'number' ? stock.mmf_aum : null;
  const aumLabel = aumRaw === null ? null
    : aumRaw >= 1e9 ? `$${(aumRaw / 1e9).toFixed(2)}B`
    : aumRaw >= 1e6 ? `$${(aumRaw / 1e6).toFixed(1)}M`
    : `$${aumRaw.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  // P158·18 (Feb 2026) — Yield-freshness stamp. `mmf_yield_updated_at`
  // is written by the nightly Yahoo-safe refresh job. Renders below
  // the metric tiles so users can see how stale the number is.
  let freshnessLabel = null;
  if (stock?.mmf_yield_updated_at) {
    try {
      const d = new Date(stock.mmf_yield_updated_at);
      if (!isNaN(d.getTime())) {
        const hoursAgo = (Date.now() - d.getTime()) / 36e5;
        if (hoursAgo < 1) freshnessLabel = 'Updated just now';
        else if (hoursAgo < 24) freshnessLabel = `Updated ${Math.round(hoursAgo)}h ago`;
        else freshnessLabel = `Yield as of ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
      }
    } catch { /* silent */ }
  }

  return (
    <div
      className="rounded-lg border-2 border-cyan-500/50 bg-cyan-50/50 dark:bg-cyan-950/20 p-3 sm:p-4 space-y-3"
      data-testid="mmf-overview-card"
    >
      <div className="flex items-center gap-2 flex-wrap">
        <Banknote className="w-4 h-4 text-cyan-600 dark:text-cyan-300" />
        <h4 className="text-[11px] font-bold uppercase tracking-widest text-cyan-700 dark:text-cyan-300">
          Money Market Fund Overview
        </h4>
        {family ? (
          <span className="text-[10px] font-semibold uppercase tracking-wide rounded-full px-2 py-0.5 bg-cyan-100 dark:bg-cyan-900/40 text-cyan-800 dark:text-cyan-200 ring-1 ring-cyan-300/60 dark:ring-cyan-700/50">
            {family} family
          </span>
        ) : null}
      </div>

      {(yield7 !== null || aumLabel !== null) && (
        <div className="grid grid-cols-2 gap-2" data-testid="mmf-metrics">
          {yield7 !== null && (
            <div className="rounded-md bg-white dark:bg-cyan-950/40 border border-cyan-500/40 p-2.5">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-cyan-700 dark:text-cyan-300">7-Day Yield</div>
              <div className="font-mono text-lg font-bold text-cyan-900 dark:text-cyan-100 tabular-nums" data-testid="mmf-yield-7day">
                {yield7.toFixed(2)}%
              </div>
            </div>
          )}
          {aumLabel !== null && (
            <div className="rounded-md bg-white dark:bg-cyan-950/40 border border-cyan-500/40 p-2.5">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-cyan-700 dark:text-cyan-300">Total Net Assets</div>
              <div className="font-mono text-lg font-bold text-cyan-900 dark:text-cyan-100 tabular-nums" data-testid="mmf-aum">
                {aumLabel}
              </div>
            </div>
          )}
        </div>
      )}
      <MmfYieldSparkline ticker={stock?.ticker} testId="mmf-modal-yield-sparkline" />
      {freshnessLabel && (
        <div className="text-[10px] text-cyan-700 dark:text-cyan-400 font-medium -mt-1" data-testid="mmf-yield-freshness">
          {freshnessLabel}
        </div>
      )}

      <p className="text-sm leading-relaxed">
        <strong>{stock.ticker}</strong> is a <strong>Money Market Fund (MMF)</strong>{' '}
        — not a stock and not an ETF. MMFs are the specific class of
        mutual funds that <strong>aim to maintain a constant $1.00 share
        price (NAV)</strong> at all times. Instead of trading second-by-second
        on an exchange, they price once a day at their closing NAV and
        pay out earnings as regular dividend distributions.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 text-xs">
        <div className="rounded-md bg-background/60 dark:bg-background/40 border border-cyan-500/30 p-2.5">
          <div className="font-semibold text-cyan-700 dark:text-cyan-300 mb-1">How it earns income</div>
          <div className="text-muted-foreground leading-relaxed">
            Holds short-dated, high-credit-quality paper — U.S. Treasury
            bills, repurchase agreements collateralised by Treasuries,
            and other government or highest-grade corporate obligations.
            Average portfolio maturity is typically 30–60 days, keeping
            interest-rate risk minimal.
          </div>
        </div>
        <div className="rounded-md bg-background/60 dark:bg-background/40 border border-cyan-500/30 p-2.5">
          <div className="font-semibold text-cyan-700 dark:text-cyan-300 mb-1">Where you'll see it</div>
          <div className="text-muted-foreground leading-relaxed">
            Brokerages (Fidelity, Schwab, Vanguard, etc.) use MMFs as
            the automatic <strong>cash-sweep</strong> destination for
            uninvested balances so your idle cash earns the current
            short-rate yield instead of nothing.
          </div>
        </div>
      </div>

      <div className="rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-300/60 dark:border-amber-700/40 p-2.5 text-xs flex items-start gap-2">
        <ShieldCheck className="w-3.5 h-3.5 text-amber-700 dark:text-amber-300 shrink-0 mt-0.5" />
        <div className="text-amber-900 dark:text-amber-100 leading-relaxed">
          <strong>Aims to maintain $1.00 — not guaranteed.</strong> SEC rule
          2a-7 governs MMFs, but a fund can still "break the buck" in
          extreme market stress (last widely-noted case: 2008). MMFs
          are not FDIC-insured; SIPC covers brokerage custody, not
          fund credit risk. Read the fund's prospectus before treating
          it as cash-equivalent.
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Link
          to="/money-market-fund-yields"
          className="inline-flex items-center gap-1.5 rounded-md bg-cyan-600 hover:bg-cyan-500 text-white text-xs font-semibold px-3 py-1.5 transition-colors"
          data-testid="mmf-hub-link"
        >
          <BarChart3 className="w-3.5 h-3.5" />
          Compare all money market funds
          <ArrowUpRight className="w-3 h-3 opacity-80" />
        </Link>
        <Link
          to="/learn/money-market-funds"
          className="inline-flex items-center gap-1.5 rounded-md border border-cyan-500/50 text-cyan-800 dark:text-cyan-200 hover:bg-cyan-100 dark:hover:bg-cyan-900/40 text-xs font-semibold px-3 py-1.5 transition-colors"
          data-testid="mmf-guide-link"
        >
          <BookOpen className="w-3.5 h-3.5" />
          The 5-letter XX ticker guide
        </Link>
      </div>

      <div className="text-[11px] text-muted-foreground leading-relaxed">
        <strong>Ticker convention:</strong> US mutual funds always have exactly 5-letter tickers.
        Money Market Funds specifically end in <strong>double-XX</strong> (SPAXX, FGXXX, VMFXX, SWVXX);
        ordinary open-end mutual funds end in a single X (VTSAX, FXAIX). DiviDrip auto-classifies
        any 3-letter-prefix + XX ticker as an MMF and swaps in this overview.
      </div>
    </div>
  );
}
